import {
  eurK,
  fullEur,
  fullEurSigned,
  monthShort,
  type OpsChantier,
  type OpsEcarts,
  type Poste,
} from '@cockpit/shared';
import { Badge, ErrorState, Spinner, Table, Td, Th, cn } from '@cockpit/ui';
import { useOpsEcarts } from '../../lib/queries.js';
import { Section } from './parts.js';

const monthName = (ym: string) => `${monthShort(ym)} ${ym.slice(0, 4)}`;

/** Écarts par poste (budget, réalisé, commandé non facturé) et vie du chantier mois par mois. */
export function FicheEcarts({ r }: { r: OpsChantier }) {
  const q = useOpsEcarts(r.code, true);
  const g = q.data?.engage ?? null;
  const postes: { k: Poste; label: string; b: number; x: number; reste: number; e: number | null }[] = [
    { k: 'ach', label: 'Achats', b: r.aR, x: r.achX, reste: r.resteA, e: g ? g.a : null },
    { k: 'st', label: 'Sous-traitance', b: r.stR, x: r.stX, reste: r.resteS, e: g ? g.s : null },
    { k: 'mo', label: 'Main-d’œuvre', b: r.moR, x: r.moX, reste: r.resteM, e: 0 },
    { k: 'oth', label: 'Autres charges', b: r.oR, x: r.oX, reste: r.resteO, e: 0 },
  ];
  const anoms = r.anoms.filter((a) => a.s !== 'i');
  return (
    <div className="flex flex-col gap-4">
      <Section title="Budget, réalisé, engagé, reste" aside="barre : réalisé, puis commandes non facturées">
        <Table minWidth={760}>
          <thead>
            <tr>
              <Th>Poste</Th>
              <Th align="right">Prévu</Th>
              <Th align="right">Révisé</Th>
              <Th align="right">Réalisé</Th>
              <Th align="right">Commandé non facturé</Th>
              <Th>Consommé du révisé</Th>
              <Th align="right">Reste du budget</Th>
              <Th>Lecture</Th>
            </tr>
          </thead>
          <tbody>
            {postes.map((p) => {
              const tol = Math.max(500, 0.05 * p.b);
              const over = p.x - p.b;
              const withE = p.e == null ? null : p.x + p.e - p.b;
              const pct = (v: number) => (p.b > 0 ? Math.min(100, Math.round((v / p.b) * 100)) : 0);
              const read =
                over > tol ? (
                  <Badge tone="danger">dépassé de {eurK(over)}</Badge>
                ) : p.e == null && (p.k === 'ach' || p.k === 'st') ? (
                  <span className="text-ink-3">{q.isError ? '—' : 'lecture…'}</span>
                ) : withE != null && withE > tol ? (
                  <Badge tone="warn">commandes au-delà du budget de {eurK(withE)}</Badge>
                ) : p.b <= 0 && p.x <= 0 ? (
                  <span className="text-ink-3">sans budget</span>
                ) : (
                  <Badge tone="success">conforme</Badge>
                );
              return (
                <tr key={p.k}>
                  <Td className="font-semibold">{p.label}</Td>
                  <Td align="right">{eurK(r.Pv[p.k])}</Td>
                  <Td align="right">{eurK(p.b)}</Td>
                  <Td align="right">{eurK(p.x)}</Td>
                  <Td align="right">{p.e == null ? (q.isError ? '—' : '…') : eurK(p.e)}</Td>
                  <Td>
                    <span className="flex h-2 w-28 overflow-hidden rounded-pill bg-surface-2" aria-hidden>
                      <i
                        className={over > tol ? 'bg-danger-600' : 'bg-azur-600'}
                        style={{ width: `${pct(p.x)}%` }}
                      />
                      {p.e ? (
                        <i
                          className={withE != null && withE > tol ? 'bg-warn-600' : 'bg-azur-300'}
                          style={{ width: `${Math.max(0, pct(p.x + p.e) - pct(p.x))}%` }}
                        />
                      ) : null}
                    </span>
                  </Td>
                  <Td align="right">{eurK(p.reste)}</Td>
                  <Td>{read}</Td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        <p className="text-[12.5px] text-ink-3">
          <b>Dépassé</b> : le réalisé dépasse déjà le budget revu. <b>Commandes au-delà du budget</b> :
          réalisé plus commandes pas encore facturées dépassent le budget revu ; le dépassement arrivera avec
          les factures. Reste du budget = révisé − réalisé.
        </p>
      </Section>

      <div className="grid gap-4 lg:grid-cols-2">
        <Section title="Anomalies" aside={anoms.length ? `${anoms.length}` : undefined}>
          {anoms.length ? (
            <ul className="list-disc pl-5 text-[13.5px] text-ink">
              {anoms.map((a) => (
                <li key={a.k + a.txt}>{a.txt}</li>
              ))}
            </ul>
          ) : (
            <p className="text-[13.5px] text-ink-3">Aucune incohérence entre Flex et la comptabilité.</p>
          )}
        </Section>
        <Section title="Dates à vérifier" aside={r.dates.length ? `${r.dates.length}` : undefined}>
          {r.dates.length ? (
            <ul className="list-disc pl-5 text-[13.5px] text-ink">
              {r.dates.map((d) => (
                <li key={d.k + d.txt}>{d.txt}</li>
              ))}
            </ul>
          ) : (
            <p className="text-[13.5px] text-ink-3">Dates de début et de fin cohérentes avec l’avancement.</p>
          )}
        </Section>
      </div>

      <Section
        title="Vie du chantier mois par mois"
        aside="facturé client, achats, sous-traitance, autres charges"
      >
        {q.isLoading ? (
          <Spinner label="Lecture des factures dans Flex…" />
        ) : q.isError ? (
          <ErrorState>{(q.error as Error).message}</ErrorState>
        ) : (
          <Months r={r} months={q.data!.months} />
        )}
      </Section>
    </div>
  );
}

function Months({ r, months }: { r: OpsChantier; months: OpsEcarts['months'] }) {
  if (!months.length)
    return <p className="text-[13.5px] text-ink-3">Aucune facture client ni fournisseur à cette date.</p>;
  const marks = new Map<string, string[]>();
  const mark = (d: string | null, t: string) => {
    if (d) marks.set(d.slice(0, 7), [...(marks.get(d.slice(0, 7)) ?? []), t]);
  };
  mark(r.deb, 'Début');
  mark(r.fin, 'Fin prévue');
  let cum = 0;
  const tot = { f: 0, a: 0, s: 0, p: 0 };
  const max = Math.max(1, ...months.map((m) => Math.max(Math.abs(m.f), Math.abs(m.a + m.s + m.p))));
  return (
    <>
      <Table minWidth={760}>
        <thead>
          <tr>
            <Th>Mois</Th>
            <Th align="right">Facturé client</Th>
            <Th align="right">Achats</Th>
            <Th align="right">Sous-traitance</Th>
            <Th align="right">Autres charges</Th>
            <Th align="right">Marge du mois</Th>
            <Th align="right">Cumul</Th>
            <Th>Repères</Th>
          </tr>
        </thead>
        <tbody>
          {months.map((m) => {
            const mg = m.f - m.a - m.s - m.p;
            cum += mg;
            tot.f += m.f;
            tot.a += m.a;
            tot.s += m.s;
            tot.p += m.p;
            return (
              <tr key={m.m}>
                <Td>{monthName(m.m)}</Td>
                <Td align="right">
                  {m.f ? fullEur(m.f) : '—'}
                  <Bar v={m.f} max={max} className="bg-azur-600" />
                </Td>
                <Td align="right">
                  {m.a ? fullEur(m.a) : '—'}
                  <Bar v={m.a + m.s + m.p} max={max} className="bg-warn-600" />
                </Td>
                <Td align="right">{m.s ? fullEur(m.s) : '—'}</Td>
                <Td align="right">{m.p ? fullEur(m.p) : '—'}</Td>
                <Td align="right" className={cn(mg < 0 && 'text-danger-700')}>
                  {fullEur(mg)}
                </Td>
                <Td align="right" className={cn('font-bold', cum < 0 && 'text-danger-700')}>
                  {fullEur(cum)}
                </Td>
                <Td className="text-[12.5px] text-ink-3">{(marks.get(m.m) ?? []).join(' · ')}</Td>
              </tr>
            );
          })}
          <tr className="font-bold">
            <Td>Total</Td>
            <Td align="right">{fullEur(tot.f)}</Td>
            <Td align="right">{fullEur(tot.a)}</Td>
            <Td align="right">{fullEur(tot.s)}</Td>
            <Td align="right">{fullEur(tot.p)}</Td>
            <Td />
            <Td align="right">{fullEurSigned(cum)}</Td>
            <Td />
          </tr>
        </tbody>
      </Table>
      <p className="text-[12.5px] text-ink-3">
        Marge du mois = facturé − achats − sous-traitance − autres charges, hors main-d’œuvre (elle n’existe
        qu’en cumul dans Flex : {fullEur(r.moX)} à ce jour). C’est la marge cumulée qu’il faut suivre.
        {r.hist === 'flex' ? ' Les mois antérieurs à 2025 ne sont pas répliqués.' : ''}
      </p>
    </>
  );
}

function Bar({ v, max, className }: { v: number; max: number; className: string }) {
  return (
    <i
      aria-hidden
      className={cn('ml-auto mt-0.5 block h-1 rounded-pill opacity-60', className)}
      style={{ width: `${(Math.abs(v) / max) * 100}%` }}
    />
  );
}
